export type WeatherCondition = 'clear' | 'cloudy' | 'rain' | 'snow' | 'storm'

export interface WeatherData {
  temperature: number
  apparentTemperature?: number // 체감 온도
  humidity?: number // 상대 습도 (%)
  windSpeed?: number // 풍속 (km/h)
  weatherCode: number
  isDay: boolean
  condition: WeatherCondition
  sunrise?: string // 일출 시각 ISO "2026-03-08T06:42"
  sunset?: string  // 일몰 시각 ISO "2026-03-08T18:23"
}

export interface GeoLocation {
  latitude: number
  longitude: number
}

const API_BASE = 'https://api.open-meteo.com/v1/forecast'
export const WEATHER_CACHE_TTL = 30 * 60 * 1000 // 30 minutes
export const DEFAULT_LOCATION: GeoLocation = { latitude: 37.5665, longitude: 126.9780 } // Seoul

export function mapWeatherCode(code: number): WeatherCondition {
  if (code <= 1) return 'clear'
  if (code <= 3 || (code >= 45 && code <= 48)) return 'cloudy'
  if ((code >= 51 && code <= 67) || (code >= 80 && code <= 82)) return 'rain'
  if ((code >= 71 && code <= 77) || (code >= 85 && code <= 86)) return 'snow'
  if (code >= 95) return 'storm'
  return 'cloudy'
}

export async function fetchWeatherData(location: GeoLocation): Promise<WeatherData> {
  const url = `${API_BASE}?latitude=${location.latitude}&longitude=${location.longitude}&current=temperature_2m,apparent_temperature,relative_humidity_2m,wind_speed_10m,weather_code,is_day&daily=sunrise,sunset&timezone=auto&forecast_days=1`
  const response = await fetch(url)
  if (!response.ok) throw new Error(`Weather API error: ${response.status}`)
  const data = await response.json()

  const current = data?.current
  if (!current || typeof current.temperature_2m !== 'number' || typeof current.weather_code !== 'number') {
    throw new Error('Invalid weather API response')
  }

  return {
    temperature: current.temperature_2m,
    apparentTemperature: current.apparent_temperature,
    humidity: current.relative_humidity_2m,
    windSpeed: current.wind_speed_10m,
    weatherCode: current.weather_code,
    isDay: current.is_day === 1,
    condition: mapWeatherCode(current.weather_code),
    sunrise: data?.daily?.sunrise?.[0],
    sunset: data?.daily?.sunset?.[0],
  }
}

export function requestGeolocation(): Promise<GeoLocation> {
  return new Promise((resolve, reject) => {
    if (!navigator.geolocation) {
      reject(new Error('Geolocation not available'))
      return
    }
    navigator.geolocation.getCurrentPosition(
      (pos) => resolve({ latitude: pos.coords.latitude, longitude: pos.coords.longitude }),
      (err) => reject(err),
      { timeout: 10000, maximumAge: 300000 }
    )
  })
}
